import {
  Component,
  HostListener,
  OnDestroy,
  OnInit,
  inject,
} from '@angular/core';
import { IFilter, IPermiso, IPopulate, IQueryParam } from 'modelos/src';
import { firstValueFrom, Subscription } from 'rxjs';
import { DialogService } from '../../../../../auxiliares/dialog/dialog.service';
import { HelperService } from '../../../../../auxiliares/servicios/helper.service';
import {
  IFiltroTabla,
  IRegExpSearch,
} from '../../../../../auxiliares/tabla/filtro/filtro.component';
import {
  IColumnas,
  ICrearBoton,
} from '../../../../../auxiliares/tabla/tabla.component';
import { LoginService } from '../../../../login/login.service';
import { FormularioDialogService } from '../../../../../auxiliares/servicios/formulario-dialog.service';
import { PermisosEfectivosDialogComponent } from './permisos-efectivos-dialog/permisos-efectivos-dialog.component';
import { Lista2Service } from '../../../../../auxiliares/servicios/lista2.service';
import { PermisosService } from '../../../../../auxiliares/servicios/http/permisos.service';
import { AgregarPermisoUsuarioComponent } from '../crear-editar-usuario/agregar-permiso-usuario/agregar-permiso-usuario.component';
import { ClienteJerarquiaService } from '../../../../../auxiliares/servicios/cliente-jerarquia.service';
import { PermisoAlcanceService } from '../../../../../auxiliares/servicios/permiso-alcance.service';
import { DetallePermisoDialogComponent } from './detalle-permiso-dialog/detalle-permiso-dialog.component';
import { escaparHtml } from '../../../../../auxiliares/html-seguro.util';

// Listado de PERMISOS (no de usuarios): los usuarios son solo credenciales sin
// tenant. Cada fila es un permiso con el usuario poblado para mostrar sus datos.
// Scopeado por idCliente del permiso + jerarquía de hijos (como toda entidad).
@Component({
  selector: 'app-listado-usuarios',
  templateUrl: './listado-usuarios.component.html',
  standalone: false,
})
export class ListadoUsuariosComponent implements OnInit, OnDestroy {
  private clienteJerarquia = inject(ClienteJerarquiaService);
  private lista2 = inject(Lista2Service);
  helper = inject(HelperService);
  private dialogService = inject(DialogService);
  private permisosService = inject(PermisosService);
  private formularios = inject(FormularioDialogService);
  private alcanceService = inject(PermisoAlcanceService);

  public idUsuario = LoginService.getUsuario()?._id;
  //// Tabla nueva
  public name = 'ListadoUsuariosComponent';
  // Una fila por permiso (con usuario poblado).
  public usuarios?: IPermiso[];
  public usuarios$?: Subscription;

  public totalCount = 0;
  public columnas?: IColumnas<IPermiso>[];
  public botonCrear: ICrearBoton = {
    mostrar: this.helper.tienePermiso('Administración - Crear usuarios'),
    tooltip: 'Crear permiso',
    color: 'primary',
    tipo: 'material',
    icon: 'add',
    accion: () => this.crear(),
  };
  // La búsqueda corre sobre el permiso: nombreUsuario (username); los datos
  // personales viven en el doc Usuario (virtual) y no se filtran acá.
  public search: IRegExpSearch = {
    fields: ['nombreUsuario'],
  };
  // Filtros sobre campos reales del permiso (ya filtran server-side).
  public clientePermiso: IFiltroTabla = {
    filter: {
      field: 'idCliente',
    },
    label: 'Cliente',
    tipo: 'cliente-selector',
  };
  // Cliente en cuya jerarquía cuelga la entidad: el cliente elegido y toda su
  // descendencia.
  public ancestro: IFiltroTabla = {
    filter: {
      field: 'idsAncestros',
      incluirPropio: true,
    },
    label: 'En la jerarquía de',
    tipo: 'cliente-selector',
  };
  public rol: IFiltroTabla = {
    elementos: [],
    filter: {
      field: 'idsRoles',
    },
    label: 'Rol',
    selectLabel: 'nombre',
    selectValue: '_id',
    tipo: 'select',
  };
  public nivel: IFiltroTabla = {
    elementos: ['Cliente', 'Grupo', 'Entidad'],
    filter: {
      field: 'nivel',
    },
    label: 'Nivel',
    tipo: 'select',
  };
  public filtros: IFiltroTabla[] = [
    this.clientePermiso,
    this.ancestro,
    this.rol,
    this.nivel,
  ];

  public filter: IFilter<IPermiso> = {};
  public populate: IPopulate[] = [
    {
      path: 'usuario',
      select: 'usuario datosPersonales identificacionInterna',
    },
    { path: 'cliente', select: 'nombre' },
    { path: 'rols', select: 'nombre acciones' },
    { path: 'grupos', select: 'nombre' },
    { path: 'activos', select: 'identificacion' },
    { path: 'luminarias', select: 'identificacion' },
    { path: 'alarmas', select: 'nombre' },
  ];
  public query: IQueryParam = {
    filter: JSON.stringify(this.filter),
    populate: JSON.stringify(this.populate),
    sort: 'nombreUsuario',
    page: 0,
    limit: +this.helper.getPageSize(this.name),
    includeChildren: true,
  };

  // Visor de foto (avatar de la columna Foto)
  public fotoVisible = false;
  public fotoUrl = '';
  public fotoAlt = '';

  @HostListener('click', ['$event'])
  onTableClick(event: MouseEvent): void {
    const fotoEl = (event.target as HTMLElement).closest(
      '[data-foto-url]',
    ) as HTMLElement | null;
    if (fotoEl) {
      this.fotoUrl = fotoEl.getAttribute('data-foto-url')!;
      this.fotoAlt = fotoEl.getAttribute('data-foto-alt') || '';
      this.fotoVisible = true;
      event.stopPropagation();
      return;
    }
    const btn = (event.target as HTMLElement).closest(
      '[data-permiso-id]',
    ) as HTMLElement | null;
    if (!btn) return;
    const permisoId = btn.getAttribute('data-permiso-id')!;
    const permiso = this.usuarios?.find((p) => p._id === permisoId);
    if (!permiso) return;
    this.abrirPermisosEfectivos(permiso, permiso.usuario?.usuario ?? '');
    event.stopPropagation();
  }

  public abrirPermisosEfectivos(permiso: IPermiso, usuario: string): void {
    this.formularios.abrir(PermisosEfectivosDialogComponent, {
      tamano: 'mediano',
      data: { permiso, usuario },
      cerrarAfuera: true,
    });
  }

  public async listar(): Promise<void> {
    this.usuarios$?.unsubscribe();
    this.usuarios$ = this.lista2
      .permisos(this.query, 30)
      .subscribe(async (data) => {
        this.totalCount = data.totalCount;
        this.usuarios = data.datos;
        // Las cámaras no tienen virtual en el permiso: se resuelven aparte.
        await this.alcanceService.precargar(data.datos);
      });
  }

  public async listarRoles(): Promise<void> {
    this.rol.elementos = (
      await firstValueFrom(this.lista2.roles({ unlimited: true }))
    ).datos;
  }

  // Acciones — alta/edición de un PERMISO (no de un usuario).
  public async crear() {
    const ref = this.formularios.abrir(AgregarPermisoUsuarioComponent, {
      tamano: 'mediano',
      data: { standalone: true },
    });
    ref.afterClosed().subscribe(async (res) => {
      if (res) await this.listar();
    });
  }

  public verDetalle(permiso: IPermiso): void {
    this.formularios.abrir(DetallePermisoDialogComponent, {
      tamano: 'mediano',
      data: { permiso },
    });
  }

  public async editar(permiso: IPermiso) {
    const ref = this.formularios.abrir(AgregarPermisoUsuarioComponent, {
      tamano: 'mediano',
      data: { standalone: true, permiso },
    });
    ref.afterClosed().subscribe(async (res) => {
      if (res) await this.listar();
    });
  }

  public async eliminar(permiso: IPermiso) {
    const nombre = permiso.usuario?.usuario || permiso.nombreUsuario || '';
    const res = await this.dialogService.confirm(
      'Eliminar Permiso',
      `¿Querés eliminar el permiso de ${nombre} sobre ${permiso.cliente?.nombre || 'el cliente'}?`,
    );
    if (!res) return;
    try {
      await this.permisosService.delete(permiso._id!);
      this.helper.notifSuccess('Permiso eliminado');
      await this.listar();
    } catch (error) {
      console.error(error);
      this.helper.notifError(error);
    }
  }

  // Tabla
  private setColumnas() {
    const columnas: IColumnas<IPermiso>[] = [
      {
        header: { label: 'Foto' },
        row: {
          html: (dato) =>
            HelperService.avatarHtml(
              dato.usuario?.datosPersonales?.foto,
              dato.usuario?.datosPersonales?.nombre || dato.usuario?.usuario,
            ),
        },
      },
      {
        header: { label: 'Usuario', sortable: true, sort: 'nombreUsuario' },
        row: {
          parse: (dato) => dato.usuario?.usuario || dato.nombreUsuario,
          noData: 'Sin usuario',
        },
      },
      {
        header: { label: 'Nombre' },
        row: {
          parse: (dato) => dato.usuario?.datosPersonales?.nombre,
          noData: 'Sin nombre',
        },
      },
      {
        header: {
          label: 'Fecha de creación',
          sortable: true,
          sort: 'fechaCreacion',
        },
        row: {
          fecha: (dato) => dato.fechaCreacion,
          noData: 'Sin fecha',
        },
      },
      {
        header: { label: 'Nivel' },
        row: {
          // Nivel + de qué es el permiso: cliente con/sin dependientes, grupos
          // o las entidades concretas cuando el nivel es Entidad.
          html: (dato) => {
            const alcance = this.alcanceService.alcance(dato);
            const resumen = this.alcanceService.resumen(alcance);
            const detalle = resumen
              ? `<div class="permiso-alcance">${alcance.nivel === 'Cliente' ? '' : `<b>${alcance.label}:</b> `}${escaparHtml(resumen)}</div>`
              : '';
            return `<div class="permiso-nivel">${alcance.nivel}</div>${detalle}`;
          },
          tooltip: (dato) => {
            const alcance = this.alcanceService.alcance(dato);
            if (alcance.nivel === 'Cliente' || !alcance.nombres.length) {
              return undefined;
            }
            return `${alcance.label}: ${escaparHtml(alcance.nombres.join(', '))}`;
          },
        },
      },
      {
        header: { label: 'Roles' },
        row: {
          html: (dato) => {
            const totalAcc = [
              ...new Set(dato.rols?.flatMap((r) => r.acciones ?? []) ?? []),
            ].length;
            const roles =
              dato.rols
                ?.map((r) => `• ${escaparHtml(r.nombre)}`)
                .join('<br>') || '<span class="no-info">Sin roles</span>';
            // HTML plano (no corren componentes): clases del sistema de botones
            const btn = `<button type="button" class="irx-boton irx-boton--texto irx-boton--compacto" data-permiso-id="${dato._id ?? ''}" title="Ver ${totalAcc} acción(es) efectiva(s)"><span class="material-symbols-outlined" aria-hidden="true">visibility</span>${totalAcc}</button>`;
            return `<div class="permiso-roles">${roles}</div>${btn}`;
          },
        },
      },
    ];

    // Cliente del permiso
    columnas.push({
      header: { label: 'Cliente' },
      row: {
        html: (dato) => this.clienteJerarquia.htmlNombre(dato.cliente),
        tooltip: (dato) => this.clienteJerarquia.tooltipJerarquia(dato.cliente),
        noData: 'Sin cliente',
      },
    });

    columnas.push({
      header: { label: 'Acciones' },
      row: {
        acciones: [
          {
            tipo: 'material',
            icon: 'visibility',
            tooltip: 'Ver detalle',
            click: (dato) => this.verDetalle(dato),
          },
          {
            tipo: 'material',
            icon: 'edit',
            tooltip: 'Editar',
            click: (dato) => this.editar(dato),
            disabled: () =>
              !this.helper.tienePermiso('Administración - Editar usuarios'),
          },
          {
            tipo: 'material',
            color: 'warn',
            icon: 'delete',
            tooltip: 'Eliminar',
            click: (dato) => this.eliminar(dato),
            disabled: (dato) => {
              if (
                !this.helper.tienePermiso('Administración - Eliminar usuarios')
              ) {
                return true;
              }
              // No permitir borrar el permiso del propio usuario logueado.
              if (dato.usuario?._id === LoginService.getUsuario()?._id) {
                return true;
              }
              return false;
            },
          },
        ],
      },
    });

    this.columnas = columnas;
  }
  // Hooks
  async ngOnInit() {
    this.setColumnas();
    await Promise.all([this.listar(), this.listarRoles()]);
  }

  ngOnDestroy() {
    this.usuarios$?.unsubscribe();
  }
}
